import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { brandColors } from '@config/brandTokens';
import { formatCatalogYearly } from '@/content/subscriptionPricing';
import {
  DISABLED_PLAN_LAUNCH_NOTE,
  clampFamilyPlanConfig,
  isPlanEnabled,
  planFromPrice,
  findPlayerPlan,
  type PlayerFamilyCatalog,
} from '@/content/subscriptionCatalog';
import type { SubscriptionOffer } from '@/content/subscriptionPricing';
import { prebookSubscription } from '@services/subscriptionService';
import type {
  FamilyPlanConfig,
  PlayerPlanId,
  PrebookIntake,
  SubscriptionCategory,
  UserSubscription,
} from '@types/index';

const SORA = '"Sora", Arial, sans-serif';

function todayInputValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const YEARS_OPTIONS = [
  { id: 'just_starting', label: 'Just starting' },
  { id: '1_2', label: 'About 1–2 years' },
  { id: '3_5', label: 'About 3–5 years' },
  { id: '5_plus', label: '5+ years' },
];

const HORIZON_OPTIONS = [
  { id: 'yes', label: 'Yes, please' },
  { id: 'exploring', label: "We're exploring" },
];

const LTAD_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'not_yet', label: 'Not yet' },
];

const HOURS_OPTIONS = [
  { id: 'under_1h', label: 'Under 1 hour' },
  { id: '1_2h', label: '1–2 hours' },
  { id: '2h_plus', label: '2 hours or more' },
];

const WELLBEING_OPTIONS = [
  { id: 'going_well', label: 'Going well' },
  { id: 'mixed', label: 'A mixed picture' },
  { id: 'finding_way', label: 'Still finding their way' },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: brandColors.white,
    borderRadius: 1.5,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: brandColors.blue },
  },
  '& .MuiInputBase-input': {
    fontSize: '0.9375rem',
    fontWeight: 400,
    color: brandColors.navy,
    py: 1.35,
  },
} as const;

function QuestionLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Typography
      component="label"
      sx={{
        display: 'block',
        fontSize: '0.9375rem',
        fontWeight: 500,
        color: brandColors.slate,
        lineHeight: 1.45,
        mb: 1,
      }}
    >
      {children}
      {required ? (
        <Box component="span" sx={{ color: brandColors.orange, fontWeight: 500, ml: 0.35 }}>
          *
        </Box>
      ) : null}
    </Typography>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.25 },
        py: { xs: 1.75, sm: 2 },
        borderRadius: 2,
        bgcolor: brandColors.white,
        border: '1px solid #e8edf4',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.75 }}>
        <Box sx={{ width: 3, height: 14, borderRadius: 8, bgcolor: brandColors.blue, flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: SORA,
            fontSize: '0.8125rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
            color: brandColors.navy,
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Stack spacing={2.25}>{children}</Stack>
    </Box>
  );
}

function ChoiceRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ id: string; label: string }>;
  onChange: (next: string) => void;
}) {
  return (
    <Box>
      <QuestionLabel>{label}</QuestionLabel>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        {options.map((option) => {
          const selected = value === option.id;
          return (
            <Chip
              key={option.id}
              label={option.label}
              onClick={() => onChange(selected ? '' : option.id)}
              variant={selected ? 'filled' : 'outlined'}
              sx={{
                height: 32,
                fontSize: '0.8125rem',
                fontWeight: 500,
                bgcolor: selected ? '#E8EEFC' : brandColors.white,
                color: selected ? brandColors.blue : brandColors.slate,
                borderColor: selected ? '#B7C8F5' : '#e2e8f0',
                '&:hover': {
                  bgcolor: selected ? '#DCE6FA' : brandColors.ice,
                  borderColor: selected ? brandColors.blue : '#cbd5e1',
                },
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

interface PrebookDialogProps {
  open: boolean;
  onClose: () => void;
  catalog: PlayerFamilyCatalog;
  offer?: SubscriptionOffer;
  initialConfig?: Partial<FamilyPlanConfig> | null;
  defaultContact?: string;
  onPrebooked: (next: UserSubscription) => void;
}

const PrebookDialog: React.FC<PrebookDialogProps> = ({
  open,
  onClose,
  catalog,
  offer,
  initialConfig,
  defaultContact,
  onPrebooked,
}) => {
  const [category, setCategory] = useState<SubscriptionCategory>('individual');
  const [planId, setPlanId] = useState<PlayerPlanId>('basic');
  const [startDate, setStartDate] = useState(todayInputValue());
  const [contact, setContact] = useState('');
  const [childrenToInclude, setChildrenToInclude] = useState('1');
  const [sportIds, setSportIds] = useState<string[]>([]);
  const [sportsOther, setSportsOther] = useState('');
  const [yearsLearning, setYearsLearning] = useState('');
  const [longHorizon, setLongHorizon] = useState('');
  const [ltadAwareness, setLtadAwareness] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('');
  const [wellbeing, setWellbeing] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const seed = clampFamilyPlanConfig(initialConfig, catalog);
    const intake = initialConfig?.prebookIntake;
    setCategory(seed.category);
    setPlanId(seed.planId);
    setStartDate(seed.startDate?.slice(0, 10) || todayInputValue());
    setContact(intake?.contact || defaultContact || '');
    setChildrenToInclude(
      intake?.childrenToInclude != null ? String(intake.childrenToInclude) : '1',
    );
    setSportIds(Array.isArray(intake?.sportIds) ? intake.sportIds : []);
    setSportsOther(intake?.sportsInterest || '');
    setYearsLearning(intake?.yearsLearning || '');
    setLongHorizon(intake?.longHorizon || '');
    setLtadAwareness(intake?.ltadAwareness || '');
    setHoursPerDay(intake?.hoursPerDay || '');
    setWellbeing(intake?.wellbeing || '');
    setError('');
  }, [open, catalog, initialConfig, defaultContact]);

  const selectedPlan = findPlayerPlan(planId, catalog);
  const from = planFromPrice(selectedPlan, catalog, offer, category);
  const planChipLabel = `${category === 'family' ? 'Family' : 'Individual'} · ${selectedPlan.name}`;
  const planOff = !isPlanEnabled(selectedPlan, category);

  const toggleSport = (id: string) => {
    setSportIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const submit = async () => {
    if (!contact.trim()) {
      setError('Add a phone or email so we can reach you.');
      return;
    }
    if (!startDate) {
      setError('Choose a subscription start date.');
      return;
    }
    setSaving(true);
    setError('');
    const seed = clampFamilyPlanConfig(initialConfig, catalog);
    const selectedNames = sportIds
      .map((id) => catalog.sports.find((sport) => sport.id === id)?.name || id)
      .filter(Boolean);
    const sportsInterest = [selectedNames.join(', '), sportsOther.trim()].filter(Boolean).join(' · ');
    const prebookIntake: PrebookIntake = {
      contact: contact.trim(),
      childrenToInclude: Number(childrenToInclude) || 1,
      sportsInterest,
      sportIds,
      yearsLearning: yearsLearning || undefined,
      longHorizon: longHorizon || undefined,
      ltadAwareness: ltadAwareness || undefined,
      hoursPerDay: hoursPerDay || undefined,
      wellbeing: wellbeing || undefined,
    };
    try {
      const next = await prebookSubscription({
        category,
        planId,
        startDate,
        familyConfig: {
          ...seed,
          category,
          planId,
          startDate,
          sportIds: seed.sportIds || [],
          prebookIntake,
        },
      });
      onPrebooked(next);
      onClose();
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      setError(anyErr.response?.data?.message || anyErr.message || 'Could not pre-book');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      scroll="paper"
      aria-labelledby="prebook-title"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: 'calc(100% - 24px)', sm: 600, md: 640 },
          maxHeight: { xs: '92vh', sm: '90vh' },
          m: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${brandColors.navy}14`,
          boxShadow: '0 16px 40px rgba(17,23,57,0.16)',
        },
      }}
    >
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${brandColors.orange} 0%, ${brandColors.blue} 62%, ${brandColors.violet} 100%)` }} />
      <Box
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          py: { xs: 2.25, sm: 2.75 },
          background: `linear-gradient(135deg, ${brandColors.navy} 0%, #1a2560 58%, ${brandColors.blue} 100%)`,
          color: brandColors.white,
        }}
      >
        <Typography
          sx={{
            fontFamily: SORA,
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            color: 'rgba(255,255,255,0.78)',
            mb: 0.75,
          }}
        >
          EncycGames
        </Typography>
        <Typography
          id="prebook-title"
          component="h2"
          sx={{
            fontFamily: SORA,
            fontWeight: 700,
            fontSize: { xs: '1.2rem', sm: '1.3rem' },
            lineHeight: 1.25,
            color: brandColors.white,
          }}
        >
          Pre-book your yearly plan
        </Typography>
        <Typography
          sx={{
            mt: 0.75,
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: 1.5,
            color: 'rgba(255,255,255,0.82)',
          }}
        >
          {planOff
            ? 'We’ll save this reservation. When the plan launches, your booking upgrades onto it.'
            : 'We’ll save the plan and start date. Sports can be chosen after payment.'}
        </Typography>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2.25, sm: 3.25 },
          py: { xs: 2.25, sm: 2.75 },
          bgcolor: brandColors.ice,
        }}
      >
        <Stack spacing={2}>
          <FormSection title="Plan & start">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'flex-start' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <QuestionLabel required>Selected plan</QuestionLabel>
                <Chip
                  label={planChipLabel}
                  sx={{
                    height: 36,
                    fontFamily: SORA,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    bgcolor: '#E8EEFC',
                    color: brandColors.navy,
                    border: '1px solid #C5D3F5',
                    '& .MuiChip-label': { px: 1.5 },
                  }}
                />
                <Typography sx={{ mt: 0.85, fontSize: '0.8125rem', fontWeight: 400, color: brandColors.slate }}>
                  {planOff
                    ? DISABLED_PLAN_LAUNCH_NOTE
                    : `${formatCatalogYearly(from.payableUsd, catalog.currency)} / year`}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <QuestionLabel required>Start date</QuestionLabel>
                <TextField
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  inputProps={{ min: todayInputValue(), 'aria-label': 'Subscription start date' }}
                  fullWidth
                  sx={fieldSx}
                />
              </Box>
            </Stack>
          </FormSection>

          <FormSection title="How to reach you">
            <Box>
              <QuestionLabel required>What’s the best phone or email to reach you?</QuestionLabel>
              <TextField
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                fullWidth
                placeholder="Phone or email"
                inputProps={{ 'aria-label': 'Best phone or email to reach you' }}
                sx={fieldSx}
              />
            </Box>
          </FormSection>

          <FormSection title="Your family & sport">
            <Box>
              <QuestionLabel>How many children would you like to include?</QuestionLabel>
              <TextField
                type="number"
                value={childrenToInclude}
                onChange={(e) => setChildrenToInclude(e.target.value)}
                inputProps={{ min: 1, max: 12, 'aria-label': 'How many children would you like to include' }}
                fullWidth
                sx={fieldSx}
              />
            </Box>
            <Box>
              <QuestionLabel>Which sport(s) is your child most excited to play?</QuestionLabel>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                {catalog.sports.map((sport) => {
                  const selected = sportIds.includes(sport.id);
                  return (
                    <Chip
                      key={sport.id}
                      label={sport.name}
                      onClick={() => toggleSport(sport.id)}
                      variant={selected ? 'filled' : 'outlined'}
                      sx={{
                        height: 32,
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        bgcolor: selected ? '#E8EEFC' : brandColors.white,
                        color: selected ? brandColors.blue : brandColors.slate,
                        borderColor: selected ? '#B7C8F5' : '#e2e8f0',
                        '&:hover': {
                          bgcolor: selected ? '#DCE6FA' : brandColors.ice,
                          borderColor: selected ? brandColors.blue : '#cbd5e1',
                        },
                      }}
                    />
                  );
                })}
              </Stack>
              <TextField
                value={sportsOther}
                onChange={(e) => setSportsOther(e.target.value)}
                fullWidth
                placeholder="Another sport, if you like"
                inputProps={{ 'aria-label': 'Another sport, if you like' }}
                sx={fieldSx}
              />
            </Box>
            <ChoiceRow
              label="About how many years has your child been enjoying this sport?"
              value={yearsLearning}
              options={YEARS_OPTIONS}
              onChange={setYearsLearning}
            />
          </FormSection>

          <FormSection title="Their journey">
            <ChoiceRow
              label="Would you like EncycGames to support your child’s sport journey over the next 5–10 years?"
              value={longHorizon}
              options={HORIZON_OPTIONS}
              onChange={setLongHorizon}
            />
            <ChoiceRow
              label="Have you come across Long-Term Athlete Development (LTAD)? We can introduce it gently."
              value={ltadAwareness}
              options={LTAD_OPTIONS}
              onChange={setLtadAwareness}
            />
            <ChoiceRow
              label="On a typical day, about how much time does your child spend on sport?"
              value={hoursPerDay}
              options={HOURS_OPTIONS}
              onChange={setHoursPerDay}
            />
            <ChoiceRow
              label="How is your child feeling in sport — body and confidence — these days?"
              value={wellbeing}
              options={WELLBEING_OPTIONS}
              onChange={setWellbeing}
            />
          </FormSection>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.25, sm: 3.25 },
          py: 2,
          bgcolor: brandColors.white,
          borderTop: '1px solid #e8edf4',
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            color: brandColors.slate,
            px: 1.5,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={submit}
          sx={{
            textTransform: 'none',
            fontFamily: SORA,
            fontWeight: 600,
            px: 2.25,
            bgcolor: brandColors.orange,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#f07a3d', boxShadow: 'none' },
          }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Pre-book'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrebookDialog;
